/**
 * API Configuration for Commercial Auto Insurance Form
 *
 * TODO: Update API_ENDPOINT with your actual endpoint URL
 */

const API_ENDPOINT = process.env.REACT_APP_COMMERCIAL_AUTO_API_ENDPOINT || process.env.REACT_APP_API_ENDPOINT || '';

const API_HEADERS = {
  'Content-Type': 'application/json',
};

/**
 * Submit the commercial auto intake form data
 *
 * @param {Object} data - The form data to submit
 * @param {Object} data.formData - Main form fields (business info, coverage, etc.)
 * @param {Array} data.vehicles - Array of vehicle items
 * @param {Array} data.drivers - Array of driver items
 * @param {number} data.totalVehicleValue - Calculated total value
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function submitCommercialAutoForm(data) {
  // If no endpoint configured, log to console and return success (dev mode)
  if (!API_ENDPOINT) {
    console.log('='.repeat(50));
    console.log('COMMERCIAL AUTO FORM SUBMISSION (No API endpoint configured)');
    console.log('='.repeat(50));
    console.log('To enable real submissions, set REACT_APP_COMMERCIAL_AUTO_API_ENDPOINT or REACT_APP_API_ENDPOINT');
    console.log('');
    console.log('Form Data:', JSON.stringify(data, null, 2));
    console.log('='.repeat(50));

    // Simulate network delay for realistic UX
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      success: true,
      message: 'Form data logged to console (API endpoint not configured)',
    };
  }

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: API_HEADERS,
      body: JSON.stringify({
        ...data,
        submittedAt: new Date().toISOString(),
        source: 'commercial-auto-intake-form',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();

    return {
      success: true,
      message: result.message || 'Form submitted successfully',
      data: result,
    };
  } catch (error) {
    console.error('Form submission error:', error);

    return {
      success: false,
      error: error.message || 'Failed to submit form. Please try again.',
    };
  }
}

/**
 * Example payload structure that will be sent:
 *
 * {
 *   formData: {
 *     // Company Details
 *     legalBusinessName: "ABC Construction LLC",
 *     dba: "",
 *     businessType: "LLC",
 *     yearStarted: "2015",
 *     yearsExperience: "10",
 *
 *     // Contact Info
 *     contactFirstName: "John",
 *     contactLastName: "Smith",
 *     contactEmail: "john@abcconstruction.com",
 *     contactPhone: "555-123-4567",
 *     website: "www.abcconstruction.com",
 *
 *     // Business Address
 *     businessStreet: "123 Main St",
 *     businessStreet2: "",
 *     businessCity: "Los Angeles",
 *     businessState: "CA",
 *     businessZip: "90001",
 *     businessCounty: "Los Angeles",
 *
 *     // Mailing Address (conditional)
 *     mailingDifferent: "No",
 *     mailingStreet: "",
 *     mailingCity: "",
 *     mailingState: "",
 *     mailingZip: "",
 *
 *     // Garaging Address (conditional)
 *     garagingDifferent: "No",
 *     garagingStreet: "",
 *     garagingCity: "",
 *     garagingState: "",
 *     garagingZip: "",
 *
 *     // Coverage Details
 *     effectiveDate: "2024-02-01",
 *     // TODO: Add coverage fields
 *
 *     additionalNotes: ""
 *   },
 *   drivers: [
 *     {
 *       id: "abc123...",
 *       firstName: "John",
 *       lastName: "Smith",
 *       dateOfBirth: "1985-06-15",
 *       licenseNumber: "D1234567",
 *       licenseState: "CA",
 *       yearsExperience: "15"
 *       // TODO: Add more driver fields
 *     }
 *   ],
 *   vehicles: [
 *     {
 *       id: "xyz789...",
 *       vehicleType: "Pickup Truck",
 *       year: "2022",
 *       make: "Ford",
 *       model: "F-150",
 *       vin: "1FTEW1EP5NFA12345",
 *       gvw: "6,500 lbs",
 *       currentValue: "45,000"
 *       // TODO: Add more vehicle fields
 *     }
 *   ],
 *   totalVehicleValue: 45000,
 *   submittedAt: "2024-01-15T10:30:00.000Z",
 *   source: "commercial-auto-intake-form"
 * }
 */
